import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBuilding, FaUsers, FaFilter, FaEye, FaExclamationTriangle } from 'react-icons/fa';
import SuperAdminSidebar from '../../components/SuperAdmin/SuperAdminSidebar';
import '../../styles/Dashboard.css';
import '../../styles/SuperAdmin/CompaniesManagement.css';

const CompaniesManagement = () => {
  const navigate = useNavigate();
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [companyStats, setCompanyStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${apiUrl}/api/companies`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch companies: ${response.status}`);
      }
      
      const data = await response.json();
   
      setCompanies(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const filterCompanies = useCallback(() => {
    let filtered = companies;

    if (searchTerm) {
      filtered = filtered.filter(c =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(c => c.status === filterStatus);
    }

    setFilteredCompanies(filtered);
  }, [companies, searchTerm, filterStatus]);

  useEffect(() => {
    if (user.role !== 'super_admin') {
      navigate('/');
      return;
    }
    fetchCompanies();
  }, [user.role, navigate, fetchCompanies]);

  useEffect(() => {
    filterCompanies();
  }, [filterCompanies]);

  const fetchCompanyStats = async (company) => {
    let stats = {
      totalUsers: 0,
      technicians: 0,
      incharges: 0,
      assetUsers: 0,
      totalAssets: 0,
      totalDepartments: 0
    };

    try {
      setStatsLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const token = sessionStorage.getItem('token');

      const statsRes = await fetch(`${apiUrl}/api/companies/${company._id}/stats`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!statsRes.ok) {
        throw new Error(`Failed to fetch stats: ${statsRes.status}`);
      }

      const statsData = await statsRes.json();
      setCompanyStats(statsData?.stats || stats);
    } catch (error) {
      console.error('Error fetching company stats:', error);
      setCompanyStats(stats);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleViewDetails = (company) => {
    setSelectedCompany(company);
    setCompanyStats(null);
    setShowDetailsModal(true);
    fetchCompanyStats(company);
  };

  const getStatusColor = (status) => {
    if (status === 'active') return 'active';
    if (status === 'idle') return 'warning';
    return 'inactive';
  };

  if (loading) {
    return (
      <div className="dashboard-wrapper">
        <SuperAdminSidebar user={user} />
        <div className="dashboard-main">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontSize: '16px', color: '#666' }}>Loading companies...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-wrapper">
      <SuperAdminSidebar user={user} />
      
      <div className="dashboard-main">
        <div style={{ marginBottom: '30px' }}>
          <h1 style={{ margin: '0 0 8px 0', color: '#1a1f3a', fontSize: '28px', fontWeight: '700' }}>Companies Management</h1>
          <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>View and manage all registered companies</p>
        </div>

        {/* Filters */}
        <div className="filters-section">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by company name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <FaFilter /> Filter by Status:
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="all">All Companies</option>
              <option value="active">Active</option>
              <option value="idle">Expired</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Companies Table */}
        <div className="companies-section">
          {filteredCompanies.length === 0 ? (
            <div className="empty-state">
              <FaBuilding />
              <p>No companies found</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="companies-table">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Plan</th>
                    <th>Users</th>
                    <th>Assets</th>
                    <th>Status</th>
                    <th>Expiry</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCompanies.map(company => (
                    <tr key={company._id || company.id} className={`status-${getStatusColor(company.status)}`}>
                      <td>
                        <div className="company-cell">
                          
                          <div className="company-info">
                            <p className="company-name">{company.name}</p>
                            <p className="company-email">{company.email}</p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="plan-badge">{company.plan}</div>
                      </td>
                      <td>
                        <div className="users-info">
                          <FaUsers /> {company.users}
                        </div>
                      </td>
                      <td>{company.assets}</td>
                      <td>
                        <span className={`status-badge status-${company.status}`}>
                          {company.status.toUpperCase()}
                        </span>
                      </td>
                      <td>
                        <div className={`expiry-cell ${company.daysRemaining < 30 ? 'warning' : ''}`}>
                          {company.daysRemaining > 0 ? (
                            <>
                              {new Date(company.expiryDate).toLocaleDateString()} <br />
                              <small>{company.daysRemaining} days left</small>
                            </>
                          ) : (
                            <>
                              <FaExclamationTriangle /> Expired
                            </>
                          )}
                        </div>
                      </td>
                      <td>
                        <button
                          className="btn-view-details"
                          onClick={() => handleViewDetails(company)}
                          title="View details"
                        >
                          <FaEye /> View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Details Modal */}
        {showDetailsModal && selectedCompany && (
          <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
            <div className="modal-content details-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Company Details</h2>
                <button className="modal-close" onClick={() => setShowDetailsModal(false)}>×</button>
              </div>

              <div className="modal-body">
                <div className="details-section">
                  <h3>Basic Information</h3>
                  <div className="details-grid">
                    <div className="detail-item">
                      <span className="label">Company Name:</span>
                      <span className="value">{selectedCompany.name}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Contact Email:</span>
                      <span className="value">{selectedCompany.email}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Contact Person:</span>
                      <span className="value">{selectedCompany.contactPerson}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Phone:</span>
                      <span className="value">{selectedCompany.phone}</span>
                    </div>
                  </div>
                </div>

                <div className="details-section">
                  <h3>Subscription Details</h3>
                  <div className="details-grid">
                    <div className="detail-item">
                      <span className="label">Plan:</span>
                      <span className="value plan-badge">{selectedCompany.plan}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Price (Monthly):</span>
                      <span className="value">${selectedCompany.price}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Joined Date:</span>
                      <span className="value">{new Date(selectedCompany.joinedDate).toLocaleDateString()}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Expiry Date:</span>
                      <span className={`value ${selectedCompany.daysRemaining < 30 ? 'warning' : ''}`}>
                        {new Date(selectedCompany.expiryDate).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="details-section">
                  <h3>Plan Limits</h3>
                  <div className="details-grid">
                    <div className="detail-item">
                      <span className="label">Users Limit:</span>
                      <span className="value">{selectedCompany.users}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Assets Limit:</span>
                      <span className="value">{selectedCompany.assets}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Status:</span>
                      <span className={`status-badge status-${selectedCompany.status}`}>
                        {selectedCompany.status.toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Company Live Statistics */}
                <div className="details-section">
                  <h3>Live Statistics</h3>
                  {statsLoading ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#666', fontSize: '14px' }}>
                      Loading...
                    </div>
                  ) : companyStats ? (
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '12px' }}>
                      <tbody>
                        <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                          <td style={{ padding: '10px 0', fontWeight: '600', color: '#333', width: '50%' }}>Total Users Created:</td>
                          <td style={{ padding: '10px 0', textAlign: 'right', color: '#1976d2', fontWeight: '600' }}>{companyStats.totalUsers}</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                          <td style={{ padding: '10px 0', fontWeight: '600', color: '#333' }}>  └─ Technicians:</td>
                          <td style={{ padding: '10px 0', textAlign: 'right', color: '#555' }}>{companyStats.technicians}</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                          <td style={{ padding: '10px 0', fontWeight: '600', color: '#333' }}>  └─ Incharges:</td>
                          <td style={{ padding: '10px 0', textAlign: 'right', color: '#555' }}>{companyStats.incharges}</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                          <td style={{ padding: '10px 0', fontWeight: '600', color: '#333' }}>  └─ Asset Users:</td>
                          <td style={{ padding: '10px 0', textAlign: 'right', color: '#555' }}>{companyStats.assetUsers}</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                          <td style={{ padding: '10px 0', fontWeight: '600', color: '#333' }}>Total Assets Created:</td>
                          <td style={{ padding: '10px 0', textAlign: 'right', color: '#f57c00', fontWeight: '600' }}>{companyStats.totalAssets}</td>
                        </tr>
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '4px', color: '#666', fontSize: '13px' }}>
                      Statistics data not available
                    </div>
                  )}
                </div>

                {selectedCompany.daysRemaining <= 30 && selectedCompany.daysRemaining > 0 && (
                  <div className="alert-box warning">
                    <FaExclamationTriangle />
                    <span>This subscription expires in {selectedCompany.daysRemaining} days. Consider reaching out to renew.</span>
                  </div>
                )}

                {selectedCompany.daysRemaining <= 0 && (
                  <div className="alert-box danger">
                    <FaExclamationTriangle />
                    <span>This subscription has expired. The company should renew to continue using the service.</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompaniesManagement;
